
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Supplier } from '../types';
import { api } from '../services/api';
import { Plus, Loader2, Trash2, Edit2, Check, X, Search } from 'lucide-react';

interface SuppliersViewProps {
    user: User;
}

export const SuppliersView: React.FC<SuppliersViewProps> = ({ user }) => {
    const { t } = useTranslation('suppliers');
    const isAssetManager = user.memberProfile?.teamRole === 'asset_manager';
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // New Supplier State
    const [newSupplier, setNewSupplier] = useState({ name: '', code: '', vatNumber: '', phone: '' });

    // Editing State
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState({ name: '', code: '', vatNumber: '', phone: '' });

    // Bulk Actions State
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        loadData();
    }, [user.id]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const agencyId = user.agencyId || user.id;
            const data = await api.getSuppliers(agencyId);
            setSuppliers(data);
            setSelectedIds(new Set()); // clear selection on load
        } catch (e) {
            console.error("Failed to load suppliers", e);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleSelect = (id: string) => {
        const newSet = new Set(selectedIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedIds(newSet);
    };

    const filteredSuppliers = suppliers.filter(s =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.vatNumber.includes(searchTerm)
    );

    const toggleSelectAll = () => {
        if (selectedIds.size === filteredSuppliers.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredSuppliers.map(s => s.id)));
        }
    };

    const handleBulkDelete = async () => {
        if (!window.confirm(t('actions.confirm_delete_multiple', 'Are you sure you want to delete the selected items?'))) return;
        setIsDeleting(true);
        try {
            const ids = Array.from(selectedIds);
            for (const id of ids) {
                await api.deleteSupplier(id);
            }
            setSelectedIds(new Set());
            await loadData();
        } catch (e: any) {
            alert(e.message || 'Failed to delete selected items');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsCreating(true);
        try {
            const agencyId = user.agencyId || user.id;
            await api.createSupplier(agencyId, newSupplier);
            await loadData();
            setNewSupplier({ name: '', code: '', vatNumber: '', phone: '' });
        } catch (e: any) {
            alert(e.message || 'Failed to create supplier');
        } finally {
            setIsCreating(false);
        }
    };

    const startEditing = (supplier: Supplier) => {
        setEditingId(supplier.id);
        setEditForm({
            name: supplier.name,
            code: supplier.code,
            vatNumber: supplier.vatNumber,
            phone: supplier.phone
        });
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditForm({ name: '', code: '', vatNumber: '', phone: '' });
    };

    const handleUpdate = async (id: string) => {
        try {
            await api.updateSupplier(id, editForm);
            setEditingId(null);
            loadData();
        } catch (e: any) {
            alert(e.message || 'Failed to update');
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm(t('actions.confirm_delete'))) {
            try {
                await api.deleteSupplier(id);
                loadData();
            } catch (e: any) {
                alert(e.message || 'Failed to delete');
            }
        }
    };

    return (
        <div className="space-y-8">
            {/* Header / Create Section */}
            {/* Header / Create Section */}
            {!isAssetManager && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
                    <h2 className="text-xl font-bold text-slate-900 mb-6">{t('title')}</h2>
                    <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('form.name')}</label>
                            <input
                                required
                                type="text"
                                value={newSupplier.name}
                                onChange={e => setNewSupplier({ ...newSupplier, name: e.target.value })}
                                className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black"
                                placeholder={t('placeholders.name')}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('form.code')}</label>
                            <input
                                required
                                type="text"
                                value={newSupplier.code}
                                onChange={e => setNewSupplier({ ...newSupplier, code: e.target.value })}
                                className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black"
                                placeholder={t('placeholders.code')}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('form.vat')}</label>
                            <input
                                required
                                type="text"
                                value={newSupplier.vatNumber}
                                onChange={e => setNewSupplier({ ...newSupplier, vatNumber: e.target.value })}
                                className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black"
                                placeholder={t('placeholders.vat')}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('form.phone')}</label>
                            <input
                                required
                                type="text"
                                value={newSupplier.phone}
                                onChange={e => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                                className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black"
                                placeholder={t('placeholders.phone')}
                            />
                        </div>
                        <div>
                            <button disabled={isCreating} type="submit" className="w-full bg-black text-white px-4 py-2.5 rounded-xl font-bold hover:opacity-80 transition flex items-center justify-center gap-2">
                                {isCreating ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />}
                                {t('form.add')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* List */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <h3 className="font-bold text-slate-900">{t('list_title')}</h3>

                        {!isAssetManager && selectedIds.size > 0 && (
                            <div className="flex items-center gap-4 ml-4">
                                <span className="text-sm text-slate-500 font-medium">
                                    {selectedIds.size} {t('selected', 'selected')}
                                </span>
                                <button
                                    onClick={handleBulkDelete}
                                    disabled={isDeleting}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-bold hover:bg-red-100 transition disabled:opacity-50"
                                >
                                    {isDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                                    {t('actions.delete_selected', 'Delete Selected')}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                        <input
                            type="text"
                            placeholder={t('search_placeholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 w-64"
                        />
                    </div>
                </div>
                {isLoading ? (
                    <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 text-slate-500">
                                <tr>
                                    {!isAssetManager && (
                                        <th className="px-6 py-4 w-12">
                                            <input
                                                type="checkbox"
                                                checked={filteredSuppliers.length > 0 && selectedIds.size === filteredSuppliers.length}
                                                onChange={toggleSelectAll}
                                                className="rounded border-slate-300 text-black focus:ring-black"
                                            />
                                        </th>
                                    )}
                                    <th className="px-6 py-4">{t('table.name')}</th>
                                    <th className="px-6 py-4">{t('table.code')}</th>
                                    <th className="px-6 py-4">{t('table.vat')}</th>
                                    <th className="px-6 py-4">{t('table.phone')}</th>
                                    {!isAssetManager && <th className="px-6 py-4 text-right">{t('table.actions')}</th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredSuppliers.length === 0 ? (
                                    <tr><td colSpan={!isAssetManager ? 6 : 4} className="px-6 py-8 text-center text-slate-400">{t('no_suppliers')}</td></tr>
                                ) : filteredSuppliers.map(s => (
                                    <tr key={s.id} className="hover:bg-slate-50">
                                        {editingId === s.id ? (
                                            <>
                                                {!isAssetManager && <td className="px-6 py-4"></td>}
                                                <td className="px-6 py-4">
                                                    <input
                                                        type="text"
                                                        value={editForm.name}
                                                        onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                                        className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <input
                                                        type="text"
                                                        value={editForm.code}
                                                        onChange={e => setEditForm(prev => ({ ...prev, code: e.target.value }))}
                                                        className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                                                    />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <input
                                                        type="text"
                                                        value={editForm.vatNumber}
                                                        onChange={e => setEditForm(prev => ({ ...prev, vatNumber: e.target.value }))}
                                                        className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                                                    />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <input
                                                        type="text"
                                                        value={editForm.phone}
                                                        onChange={e => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                                                        className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </td>
                                                {!isAssetManager && (
                                                    <td className="px-6 py-4 text-right flex justify-end gap-2">
                                                        <button onClick={() => handleUpdate(s.id)} className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition" title={t('actions.save')}>
                                                            <Check size={16} />
                                                        </button>
                                                        <button onClick={cancelEditing} className="p-2 bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200 transition" title={t('actions.cancel')}>
                                                            <X size={16} />
                                                        </button>
                                                    </td>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {!isAssetManager && (
                                                    <td className="px-6 py-4">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIds.has(s.id)}
                                                            onChange={() => toggleSelect(s.id)}
                                                            className="rounded border-slate-300 text-black focus:ring-black"
                                                        />
                                                    </td>
                                                )}
                                                <td className="px-6 py-4 font-bold text-slate-900">{s.name}</td>
                                                <td className="px-6 py-4 font-mono text-slate-600">{s.code}</td>
                                                <td className="px-6 py-4 font-mono text-slate-600">{s.vatNumber}</td>
                                                <td className="px-6 py-4 text-slate-600">{s.phone}</td>
                                                {!isAssetManager && (
                                                    <td className="px-6 py-4 text-right flex justify-end gap-2">
                                                        <button onClick={() => startEditing(s)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition" title={t('actions.edit')}>
                                                            <Edit2 size={16} />
                                                        </button>
                                                        <button onClick={() => handleDelete(s.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition" title={t('actions.delete')}>
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </td>
                                                )}
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};
